import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useStreak } from '../contexts/StreakContext';

export interface ChapterContentProps {
  content: string[];
  title: string;
  onContentComplete: () => void;
  onProgressUpdate?: (progress: number) => void;
}

const ChapterContent: React.FC<ChapterContentProps> = ({
  content,
  title,
  onContentComplete,
  onProgressUpdate
}) => {
  const { trackActivity } = useStreak();
  const contentRef = useRef<HTMLDivElement>(null);
  const paragraphRefs = useRef<(HTMLParagraphElement | null)[]>([]);

  // Use refs so scroll handler always reads latest values without re-attaching
  const hasCompletedRef = useRef(false);
  const lastReportedProgressRef = useRef(0);
  const onContentCompleteRef = useRef(onContentComplete);
  const onProgressUpdateRef = useRef(onProgressUpdate);
  const trackActivityRef = useRef(trackActivity);

  // Keep refs in sync with latest props/state
  useEffect(() => { onContentCompleteRef.current = onContentComplete; }, [onContentComplete]);
  useEffect(() => { onProgressUpdateRef.current = onProgressUpdate; }, [onProgressUpdate]);
  useEffect(() => { trackActivityRef.current = trackActivity; }, [trackActivity]);

  // Track reading progress based on scroll position — attached once, never re-created
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const container = contentRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;

      // If content fits in viewport, don't immediately set to 100%
      // Let the paragraph-based tracking handle it instead
      if (scrollHeight === 0) {
        return;
      }

      const progress = Math.min(100, Math.max(0, (scrollTop / scrollHeight) * 100));

      // Update progress bar (throttled to avoid excessive re-renders)
      if (Math.abs(progress - lastReportedProgressRef.current) > 1) {
        lastReportedProgressRef.current = progress;

        if (onProgressUpdateRef.current) {
          onProgressUpdateRef.current(progress);
        }
      }

      // Mark as complete when user has scrolled to 90% or more — fires only once
      if (progress >= 90 && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        onContentCompleteRef.current();
        trackActivityRef.current('chapter_read', 1);
      }
    };

    const container = contentRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });

      return () => container.removeEventListener('scroll', handleScroll);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track reading progress based on visible paragraphs using IntersectionObserver
  useEffect(() => {
    // Only use IntersectionObserver if available (not in test environment)
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const observerOptions = {
        root: contentRef.current,
        rootMargin: '0px',
        threshold: 0.3 // Paragraph is considered read when 30% visible
      };

      const readParagraphs = new Set<number>();

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const index = paragraphRefs.current.indexOf(entry.target as HTMLParagraphElement);
          if (index !== -1) {
            if (entry.isIntersecting) {
              readParagraphs.add(index);
            }
          }
        });

        if (content.length > 0) {
          const progressByParagraphs = (readParagraphs.size / content.length) * 100;

          // Mark as complete when 80% of paragraphs have been seen — fires only once via ref
          if (progressByParagraphs >= 80 && !hasCompletedRef.current) {
            hasCompletedRef.current = true;
            onContentCompleteRef.current();
            trackActivityRef.current('chapter_read', 1);
          }
        }
      }, observerOptions);

      // Observe all paragraphs
      paragraphRefs.current.forEach((ref) => {
        if (ref) observer.observe(ref);
      });

      return () => observer.disconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.length]);

  // Group content into sections based on headers (lines starting with #)
  const sections = content.reduce((acc: { title: string | null; paragraphs: string[] }[], paragraph) => {
    if (paragraph.startsWith('#')) {
      // This is a header, create new section
      acc.push({
        title: paragraph.replace(/^#+\s*/, ''),
        paragraphs: []
      });
    } else if (acc.length === 0) {
      // First paragraph without header
      acc.push({
        title: null,
        paragraphs: [paragraph]
      });
    } else {
      // Add to current section
      acc[acc.length - 1].paragraphs.push(paragraph);
    }
    return acc;
  }, []);

  let paragraphIndex = 0;

  return (
    <div className="flex flex-col h-full" role="main" aria-labelledby="chapter-title">
      {/* Scrollable content area */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pt-4 pb-32 sm:py-8
                  bg-background dark:bg-[#142328]"
        data-testid="chapter-content-scroll"
      >
        <div className="max-w-4xl mx-auto">
          {/* Title Header */}
          <header className="mb-8 sm:mb-12 pb-6 
                           border-b border-gray-200 dark:border-gray-700/50">
            <h1 id="chapter-title" className="text-2xl sm:text-3xl lg:text-4xl font-bold 
                                          text-gray-900 dark:text-white 
                                          leading-tight text-center">
              {title}
            </h1>
            <div className="mt-4 flex items-center justify-center gap-4 text-sm 
                          text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                {content.length} {content.length === 1 ? 'sekce' : content.length < 5 ? 'sekce' : 'sekcí'}
              </span>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ~{Math.ceil(content.length * 0.5)} min čtení
              </span>
            </div>
          </header>

          <article className="max-w-none">
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-8 sm:mb-12">
                {/* Section Title */}
                {section.title && (
                  <h2 className="text-xl sm:text-2xl font-semibold 
                               text-gray-900 dark:text-white 
                               mb-4 sm:mb-6 pb-3 
                               border-b border-gray-200 dark:border-gray-700/30">
                    {section.title}
                  </h2>
                )}

                {/* Section Content */}
                <div className="space-y-4 sm:space-y-6">
                  {section.paragraphs.map((paragraph, pIndex) => {
                    const currentIndex = paragraphIndex++;
                    return (
                      <div
                        key={`${sectionIndex}-${pIndex}`}
                        ref={(el) => {
                          paragraphRefs.current[currentIndex] = el as HTMLParagraphElement;
                        }}
                        className="text-base sm:text-lg leading-[1.8] 
                                  text-gray-700 dark:text-gray-300 font-normal
                                  selection:bg-orange-200 dark:selection:bg-orange-500/30 
                                  selection:text-gray-900 dark:selection:text-white
                                  prose prose-lg dark:prose-invert max-w-none
                                  prose-strong:text-gray-900 dark:prose-strong:text-white 
                                  prose-strong:font-semibold
                                  prose-em:text-orange-600 dark:prose-em:text-orange-400 
                                  prose-em:font-medium
                                  prose-a:text-orange-600 dark:prose-a:text-orange-400 
                                  prose-a:no-underline hover:prose-a:underline
                                  prose-ul:space-y-2 prose-li:marker:text-orange-500"
                        data-testid={`paragraph-${currentIndex}`}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => <p className="mb-0">{children}</p>,
                            strong: ({ children }) => <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>,
                            em: ({ children }) => <em className="italic text-orange-600 dark:text-orange-400 font-medium">{children}</em>,
                            ul: ({ children }) => <ul className="list-disc list-outside ml-5 space-y-2 my-4">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-outside ml-5 space-y-2 my-4">{children}</ol>,
                            li: ({ children }) => <li className="pl-2">{children}</li>,
                            h3: ({ children }) => <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">{children}</h3>,
                            h4: ({ children }) => <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mt-4 mb-2">{children}</h4>,
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-4 border-orange-500 pl-4 italic 
                                                   text-gray-500 dark:text-gray-400 my-4">
                                {children}
                              </blockquote>
                            ),
                            code: ({ children }) => (
                              <code className="bg-gray-100 dark:bg-gray-800 
                                             px-1.5 py-0.5 rounded text-sm 
                                             text-orange-600 dark:text-orange-400 font-mono">
                                {children}
                              </code>
                            )
                          }}
                        >
                          {paragraph}
                        </ReactMarkdown>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </article>


        </div>
      </div>
    </div>
  );
};

export default ChapterContent;